import {
    FormProvider,
    RHFSelect,
    RHFTextField,
} from "@convin/components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import useNodeContext from "../../hooks/useNodeContext";
import { predefinedFunctionOptions } from "../../constants";
import {
    PredefinedFuncBlockFormType,
    SingleResponseNode,
} from "../../../types/bert";
import { useReactFlowStateContextProvider } from "../../hooks/useReactFlowStateContextProvider";
import BlockHeader from "./BlockHeader";
import { isDefined } from "@convin/utils/helper/common.helper";
import { useEffect, useMemo, useRef } from "react";

export default function PredefinedFuncBlock({
    showCreateButton = true,
}: {
    showCreateButton?: boolean;
}) {
    const canUpdate = useRef<boolean>(false);
    const {
        sourceId,
        direction,
        onNodeSaveCallBack,
        nodeIdToUpdate,
        nodeDataToUpdate,
        blockParentId,
    } = useNodeContext();

    const { createSingleResponseNode, updateSingleResponseNode } =
        useReactFlowStateContextProvider();

    const generateSchemaObject = useMemo(() => {
        const schemaObject: Record<string, Yup.AnySchema> = {
            name: Yup.string().required("Name is Required"),
            function_type: Yup.string().required("Function is Required"),
            reference: Yup.string()
                .nullable()
                .when("function_type", {
                    is: (
                        function_type: PredefinedFuncBlockFormType["function_type"]
                    ) => function_type === "account_index",
                    then: (s) => s.required("Required"),
                    otherwise: (s) => s,
                }),
            operator: Yup.string()
                .nullable()
                .when("function_type", {
                    is: (
                        function_type: PredefinedFuncBlockFormType["function_type"]
                    ) =>
                        function_type === "owner_talk_ratio" ||
                        function_type === "duration",
                    then: (s) => s.required("Required"),
                    otherwise: (s) => s,
                }),
            value: Yup.number()
                .nullable()
                .transform((value, originalValue) =>
                    String(originalValue).trim() === "" ? null : value
                )
                .when("function_type", {
                    is: (
                        function_type: PredefinedFuncBlockFormType["function_type"]
                    ) => function_type === "owner_talk_ratio",
                    then: (s) =>
                        s
                            .required("Required")
                            .min(0, "Enter between 0 to 100")
                            .max(100, "Enter between 0 to 100"),
                    otherwise: (s) =>
                        s.when("function_type", {
                            is: (
                                function_type: PredefinedFuncBlockFormType["function_type"]
                            ) =>
                                function_type === "duration" ||
                                function_type === "account_index",
                            then: (s) =>
                                s.required("Required").min(0, "Enter above 0"),
                            otherwise: (s) => s,
                        }),
                }),
            min: Yup.number()
                .transform((value, originalValue) =>
                    String(originalValue).trim() === "" ? null : value
                )
                .nullable()
                .typeError("must be a number"),
            max: Yup.number()
                .transform((value, originalValue) =>
                    String(originalValue).trim() === "" ? null : value
                )
                .nullable()
                .typeError("must be a number"),
        };

        return Yup.object()
            .shape(schemaObject)
            .test(
                "min-or-max-required",
                "Either Min or Max is required",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                function (value: any) {
                    if (
                        [
                            "owner_talk_ratio",
                            "duration",
                            "account_index",
                        ].includes(value.function_type)
                    )
                        return true;

                    if (value.min === null && value.max === null) {
                        return this.createError({
                            path: "min",
                            message: "Either Min or Max is required",
                        });
                    }
                    return true;
                }
            )
            .test(
                "min-less-than-max",
                "Min should not exceed Max",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                function (value: any) {
                    if (
                        [
                            "owner_talk_ratio",
                            "duration",
                            "account_index",
                        ].includes(value.function_type)
                    )
                        return true;

                    if (
                        value.min !== null &&
                        value.max !== null &&
                        value.min > value.max
                    ) {
                        return this.createError({
                            path: "min",
                            message: "Min should not exceed Max",
                        });
                    }
                    return true;
                }
            )
            .test(
                "min-max-not-equal",
                "Min and Max should not be equal",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                function (value: any) {
                    if (
                        [
                            "owner_talk_ratio",
                            "duration",
                            "account_index",
                        ].includes(value.function_type)
                    )
                        return true;

                    if (value.min === value.max) {
                        return this.createError({
                            path: "min",
                            message: "Min and Max should not be equal",
                        });
                    }
                    return true;
                }
            );
    }, []);

    const methods = useForm<PredefinedFuncBlockFormType>({
        resolver: yupResolver(generateSchemaObject),
        values: isDefined(nodeDataToUpdate)
            ? { ...(nodeDataToUpdate as PredefinedFuncBlockFormType) }
            : {
                  name: "",
                  function_type: "",
                  min: null,
                  max: null,
                  operator: null,
                  value: null,
                  reference: null,
              },
    });

    const onSubmit = async (values: PredefinedFuncBlockFormType) => {
        if (isDefined(nodeIdToUpdate)) {
            updateSingleResponseNode<PredefinedFuncBlockFormType>({
                data: values,
                sourceId: nodeIdToUpdate,
                blockParentId,
            });
        } else
            createSingleResponseNode<SingleResponseNode<"function">>({
                data: {
                    type: "function",
                    is_not: false,
                    metadata: values,
                },
                sourceId,
                direction,
                blockParentId,
            });
        onNodeSaveCallBack();
    };

    const { handleSubmit, watch, setValue } = methods;
    const functionType = watch("function_type");

    useEffect(() => {
        if (canUpdate.current) {
            setValue("min", null);
            setValue("max", null);
        } else {
            canUpdate.current = true;
        }
    }, [functionType]);

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <BlockHeader
                title="Predefined Function"
                showCreateButton={showCreateButton}
            />
            <Box
                sx={{
                    width: "100%",
                }}
            >
                <Stack gap={1.5}>
                    <RHFTextField
                        name="name"
                        className="w-full"
                        variant="outlined"
                        size="small"
                        placeholder="Block Name"
                        label="Block Name"
                    />
                    <RHFSelect
                        name="function_type"
                        className="w-full nodrag nopan"
                        variant="outlined"
                        size="small"
                        label="Select predefined function"
                        options={predefinedFunctionOptions}
                    />
                    {functionType === "duration" ||
                    functionType === "owner_talk_ratio" ||
                    functionType === "account_index" ? (
                        <Stack gap={1} direction="row">
                            {functionType === "duration" ||
                            functionType === "owner_talk_ratio" ? (
                                <RHFSelect
                                    name="operator"
                                    className="w-full nodrag nopan"
                                    variant="outlined"
                                    size="small"
                                    label="Select operator"
                                    options={[
                                        {
                                            id: "gt",
                                            label: "Greater Than",
                                        },
                                        {
                                            id: "lt",
                                            label: "Less Than",
                                        },
                                        {
                                            id: "eq",
                                            label: "Equals",
                                        },
                                        {
                                            id: "gte",
                                            label: "Greater Than or Equal To",
                                        },
                                        {
                                            id: "lte",
                                            label: "Less Than or Equal To",
                                        },
                                    ]}
                                />
                            ) : (
                                <RHFSelect
                                    name="reference"
                                    className="w-full nodrag nopan"
                                    variant="outlined"
                                    size="small"
                                    label="Reference"
                                    options={[
                                        {
                                            id: "start",
                                            label: "From Start",
                                        },
                                        {
                                            id: "end",
                                            label: "From End",
                                        },
                                    ]}
                                />
                            )}

                            {functionType === "duration" ||
                            functionType === "owner_talk_ratio" ||
                            functionType === "account_index" ? (
                                <RHFTextField
                                    name="value"
                                    className="w-full nowheel nodrag nopan"
                                    variant="outlined"
                                    size="small"
                                    label="Value"
                                    type="number"
                                    slotProps={{
                                        input: {
                                            inputProps: { min: 0 },
                                        },
                                    }}
                                />
                            ) : (
                                <></>
                            )}
                        </Stack>
                    ) : (
                        <Stack gap={1} direction="row">
                            <RHFTextField
                                name="min"
                                className="w-full nowheel nodrag nopan"
                                variant="outlined"
                                size="small"
                                label={"Min"}
                                type="number"
                                InputProps={{ inputProps: { min: 0 } }}
                            />

                            <RHFTextField
                                name="max"
                                className="w-full nowheel nodrag nopan"
                                variant="outlined"
                                size="small"
                                label="Max"
                                type="number"
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Stack>
                    )}
                </Stack>
            </Box>
        </FormProvider>
    );
}
